import { useCallback, useState } from 'react'
import { Badge, Code, Stack, Text } from '@mantine/core'
import { CPG_AUDIT_QUERY_FIELDS } from '@behuman/core-contract'
import { useClient } from '@/hooks/useClient'
import { SelectRecord } from '@/components/ui/SelectRecord'
import type { RecordColumn, RecordSearchParams } from '@/components/ui/SelectRecord'
import { toRecordQuery, toSearchResult } from './search'
import { actorLabel, type CpgAuditRow } from './hooks'

const ACTION_COLORS: Record<CpgAuditRow['action'], string> = {
  insert: 'green',
  update: 'blue',
  delete: 'red',
}

const COLUMNS: RecordColumn<CpgAuditRow>[] = [
  { key: 'id', header: '#', width: 70 },
  {
    key: 'createdAt',
    header: 'When',
    width: 170,
    display: (r) => new Date(r.createdAt).toLocaleString(),
  },
  { key: 'actor', header: 'Actor', width: 160, display: (r) => actorLabel(r.actor) },
  { key: 'entityType', header: 'Entity', width: 190 },
  { key: 'entityId', header: 'Entity #', width: 90 },
  {
    key: 'action',
    header: 'Action',
    width: 100,
    render: (r) => (
      <Badge color={ACTION_COLORS[r.action]} variant="light" radius="sm" size="sm">
        {r.action}
      </Badge>
    ),
  },
]

function ValueBlock({ label, value }: { label: string; value: unknown }) {
  return (
    <div>
      <Text size="xs" fw={600} tt="uppercase" c="dimmed" className="mb-1">
        {label}
      </Text>
      {value === null || value === undefined ? (
        <Text size="sm" c="dimmed">
          —
        </Text>
      ) : (
        <Code block className="text-xs">
          {JSON.stringify(value, null, 2)}
        </Code>
      )}
    </div>
  )
}

/**
 * Read-only view over the append-only cpg_registry_audit trail. Left: the entries (every
 * column filterable/orderable, newest first); right: the selected entry's old → new values.
 */
export function AuditView() {
  const client = useClient()
  const [selected, setSelected] = useState<CpgAuditRow | null>(null)

  const search = useCallback(
    async (params: RecordSearchParams<CpgAuditRow>) => {
      const res = await client.listCpgAudit(toRecordQuery(params, CPG_AUDIT_QUERY_FIELDS))
      return toSearchResult(res)
    },
    [client],
  )

  return (
    <div className="flex h-full min-h-0">
      <div className="w-[60%] min-w-0 border-r border-gray-200">
        <SelectRecord<CpgAuditRow>
          columns={COLUMNS}
          getRowId={(r) => String(r.id)}
          search={search}
          onSelect={setSelected}
          recordNoun="audit entry"
          slug="cpg-audit"
          pageSize={40}
          showGlobalSearch={false}
          showSelectionBar={false}
          footerHint={null}
        />
      </div>
      <div className="w-[40%] min-w-0 overflow-y-auto p-4">
        {selected ? (
          <Stack gap="sm">
            <div>
              <Text fw={600}>
                {selected.entityType} #{selected.entityId}
              </Text>
              <Text size="sm" c="dimmed">
                {selected.action} · {new Date(selected.createdAt).toLocaleString()} ·{' '}
                {actorLabel(selected.actor)}
              </Text>
            </div>
            <ValueBlock label="Old value" value={selected.oldValue} />
            <ValueBlock label="New value" value={selected.newValue} />
          </Stack>
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-gray-500">
            Select an entry to inspect its change.
          </div>
        )}
      </div>
    </div>
  )
}
